// This file wraps the sonner toast functionality to disable notifications but keep logging

// Function to log toast content to console
const logToastToConsole = (title: string, description?: string, type: string = 'default') => {
  const icon = type === 'error' ? '❌' : type === 'success' ? '✅' : '🔔';
  console.log(`${icon} SONNER TOAST: ${title}${description ? ` - ${description}` : ''}`);
};

// Create mock implementations that only log to console
const toast = {
  // Basic toast
  default: (title: string, options?: any) => {
    logToastToConsole(title, options?.description);
    return { id: Date.now() };
  },
  
  // Success toast
  success: (title: string, options?: any) => {
    logToastToConsole(title, options?.description, 'success');
    return { id: Date.now() };
  },
  
  // Error toast
  error: (title: string, options?: any) => {
    logToastToConsole(title, options?.description, 'error');
    return { id: Date.now() };
  },
  
  // Info toast
  info: (title: string, options?: any) => {
    logToastToConsole(title, options?.description, 'info');
    return { id: Date.now() };
  },
  
  // Warning toast
  warning: (title: string, options?: any) => {
    logToastToConsole(title, options?.description, 'warning');
    return { id: Date.now() };
  },
  
  // Loading toast
  loading: (title: string, options?: any) => {
    logToastToConsole(`Loading: ${title}`, options?.description, 'loading');
    return { id: Date.now() };
  },
  
  // Promise toast - returns the promise to maintain API compatibility
  promise: <T>(promise: Promise<T>, options?: any) => {
    logToastToConsole(`Promise: ${options?.loading || 'Loading...'}`, undefined, 'loading');
    promise
      .then(() => logToastToConsole(`Promise Resolved: ${options?.success || 'Success!'}`, undefined, 'success'))
      .catch(() => logToastToConsole(`Promise Rejected: ${options?.error || 'Error!'}`, undefined, 'error'));
    return promise;
  },
  
  // Dismiss functionality
  dismiss: (id?: string | number) => {
    console.log(`Toast dismissed${id ? `: ${id}` : ''}`);
  },
  
  // Other methods for API completeness
  custom: (render: any, options?: any) => {
    logToastToConsole('Custom toast', options?.description);
    return { id: Date.now() };
  }
};

// Add function call support (treating the toast object as a function)
// This is a TypeScript workaround for objects that can be called as functions
const toastWithFunctionality = Object.assign(
  // Function that can be called directly
  (title: string, options?: any) => {
    return toast.default(title, options);
  },
  // All the properties of the original toast object
  toast
);

export { toastWithFunctionality as toast };
